import { useMemo } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useQuery } from "@tanstack/react-query"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import ToggleButton from "components/Common/ToggleButton"
import { getUsers } from "api/userApi"
import {
  useCreateReminder,
  useEditReminder,
  useReminderMeta,
} from "hooks/reminderHook"

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/

const toOptions = (list = []) => list.map(v => ({ value: v, label: v }))

const emptyForm = {
  showroom_id: "",
  title_en: "",
  title_bn: "",
  body_en: "",
  body_bn: "",
  frequency: "daily",
  days_of_week: [],
  times: [],
  day_of_month: "",
  target_type: "all",
  target_permission: "",
  target_user_ids: [],
  route: "/notifications",
  skip_friday: true,
  active: true,
}

const validationSchema = Yup.object({
  title_en: Yup.string().required("Title is required"),
  body_en: Yup.string().required("Message is required"),
  frequency: Yup.string().oneOf(["daily", "weekly", "monthly"]).required(),
  days_of_week: Yup.array().when("frequency", {
    is: "weekly",
    then: schema => schema.min(1, "Pick at least one weekday"),
  }),
  times: Yup.array()
    .of(Yup.string().matches(TIME_RE, "Use HH:MM (24h)"))
    .when("frequency", {
      is: f => f === "daily" || f === "weekly",
      then: schema => schema.min(1, "Add at least one time"),
    }),
  day_of_month: Yup.number()
    .transform((val, orig) =>
      orig === "" || orig === null || orig === undefined ? null : val
    )
    .nullable()
    .when("frequency", {
      is: "monthly",
      then: schema =>
        schema
          .typeError("Pick a day of the month")
          .required("Pick a day of the month")
          .min(1)
          .max(31),
    }),
  target_type: Yup.string().oneOf(["all", "permission", "users"]).required(),
  target_permission: Yup.string().when("target_type", {
    is: "permission",
    then: schema => schema.required("Select a permission"),
  }),
  target_user_ids: Yup.array().when("target_type", {
    is: "users",
    then: schema => schema.min(1, "Select at least one user"),
  }),
})

const ReminderAddEdit = ({ isOpen, toggle, reminder }) => {
  const isEdit = Boolean(reminder?.id)
  const { data: metaResp } = useReminderMeta(isOpen)
  const meta = metaResp?.data ?? {}

  const createMutation = useCreateReminder()
  const editMutation = useEditReminder()

  const { data: usersResp } = useQuery({
    queryKey: ["reminder-user-options"],
    queryFn: () => getUsers({ per_page: 200 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const userOptions = useMemo(
    () =>
      (usersResp?.data?.data ?? usersResp?.data ?? []).map(u => ({
        value: u.id,
        label: u.name ? `${u.name}${u.email ? ` (${u.email})` : ""}` : `#${u.id}`,
      })),
    [usersResp]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: reminder
      ? {
          ...emptyForm,
          ...reminder,
          showroom_id: reminder.showroom_id ?? "",
          day_of_month: reminder.day_of_month ?? "",
          days_of_week: reminder.days_of_week ?? [],
          times: reminder.times ?? [],
          target_user_ids: reminder.target_user_ids ?? [],
        }
      : emptyForm,
    validationSchema,
    onSubmit: async values => {
      const payload = {
        showroom_id: values.showroom_id || null,
        key: values.key || null,
        title_en: values.title_en,
        title_bn: values.title_bn || null,
        body_en: values.body_en,
        body_bn: values.body_bn || null,
        frequency: values.frequency,
        days_of_week: values.frequency === "weekly" ? values.days_of_week : [],
        times:
          values.frequency === "daily" || values.frequency === "weekly"
            ? values.times
            : [],
        day_of_month:
          values.frequency === "monthly" ? Number(values.day_of_month) : null,
        target_type: values.target_type,
        target_permission:
          values.target_type === "permission" ? values.target_permission : null,
        target_user_ids:
          values.target_type === "users" ? values.target_user_ids : null,
        route: values.route || null,
        skip_friday: values.skip_friday,
        active: values.active,
      }
      try {
        if (isEdit) {
          await editMutation.mutateAsync({ id: reminder.id, ...payload })
          toast.success("Reminder updated")
        } else {
          await createMutation.mutateAsync(payload)
          toast.success("Reminder created")
        }
        toggle()
      } catch (err) {
        toast.error(err?.response?.data?.message || "Save failed")
      }
    },
  })

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik
  const showTimes = values.frequency === "daily" || values.frequency === "weekly"

  const FIELD_LABELS = {
    title_en: "Title (English)",
    body_en: "Message (English)",
    frequency: "Frequency",
    days_of_week: "Weekdays",
    times: "Times",
    day_of_month: "Day of month",
    target_type: "Target",
    target_permission: "Recipient permission",
    target_user_ids: "Users",
  }

  const errorList = Object.entries(errors)
    .map(([key, val]) => {
      const msg = Array.isArray(val) ? val.find(Boolean) : val
      return msg ? `${FIELD_LABELS[key] ?? key}: ${msg}` : null
    })
    .filter(Boolean)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>{isEdit ? "Edit" : "Add"} Reminder</ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <label className="form-label">Title (English) *</label>
              <InputField
                name="title_en"
                value={values.title_en}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={touched.title_en && !!errors.title_en}
              />
              {touched.title_en && errors.title_en && (
                <div className="text-danger small mb-2">{errors.title_en}</div>
              )}
            </Col>
            <Col md={6}>
              <label className="form-label">Title (Bangla)</label>
              <InputField name="title_bn" value={values.title_bn} onChange={handleChange} />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <label className="form-label">Message (English) *</label>
              <InputField
                type="textarea"
                name="body_en"
                value={values.body_en}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={touched.body_en && !!errors.body_en}
              />
              {touched.body_en && errors.body_en && (
                <div className="text-danger small mb-2">{errors.body_en}</div>
              )}
            </Col>
            <Col md={6}>
              <label className="form-label">Message (Bangla)</label>
              <InputField
                type="textarea"
                name="body_bn"
                value={values.body_bn}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormikCreatableSelect
                label="Frequency"
                name="frequency"
                formik={formik}
                options={toOptions(meta.frequencies ?? ["daily", "weekly", "monthly"])}
              />
            </Col>
            <Col md={4}>
              <FormikCreatableSelect
                label="Showroom (blank = all)"
                name="showroom_id"
                formik={formik}
                options={(meta.showrooms ?? []).map(s => ({ value: s.id, label: s.name }))}
              />
            </Col>
            <Col md={4}>
              <label className="form-label">Deep link route</label>
              <InputField name="route" value={values.route} onChange={handleChange} />
            </Col>
          </Row>

          {values.frequency === "weekly" && (
            <FormikCreatableSelect
              label="Weekdays"
              name="days_of_week"
              formik={formik}
              isMulti
              options={toOptions(meta.weekdays ?? ["sat", "sun", "mon", "tue", "wed", "thu", "fri"])}
            />
          )}

          {values.frequency === "monthly" && (
            <Row>
              <Col md={4}>
                <label className="form-label">Day of month *</label>
                <InputField
                  type="number"
                  name="day_of_month"
                  min={1}
                  max={31}
                  value={values.day_of_month}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={touched.day_of_month && !!errors.day_of_month}
                />
                {touched.day_of_month && errors.day_of_month && (
                  <div className="text-danger small">{errors.day_of_month}</div>
                )}
              </Col>
            </Row>
          )}

          {showTimes && (
            <>
              <FormikCreatableSelect
                label="Times (type HH:MM, press enter)"
                name="times"
                formik={formik}
                isMulti
                options={toOptions(values.times ?? [])}
              />
              {touched.times && errors.times && (
                <div className="text-danger small mb-2">
                  {Array.isArray(errors.times) ? errors.times.find(Boolean) : errors.times}
                </div>
              )}
            </>
          )}

          <Row>
            <Col md={4}>
              <FormikCreatableSelect
                label="Target"
                name="target_type"
                formik={formik}
                options={toOptions(meta.target_types ?? ["all", "permission", "users"])}
              />
            </Col>
            {values.target_type === "permission" && (
              <Col md={8}>
                <FormikCreatableSelect
                  label="Recipient permission"
                  name="target_permission"
                  formik={formik}
                  options={(meta.notification_permissions ?? []).map(p => ({
                    value: p.slug,
                    label: p.name,
                  }))}
                />
              </Col>
            )}
            {values.target_type === "users" && (
              <Col md={8}>
                <label className="form-label">Users</label>
                <Select
                  isMulti
                  options={userOptions}
                  value={userOptions.filter(o => values.target_user_ids.includes(o.value))}
                  onChange={selected =>
                    setFieldValue("target_user_ids", (selected ?? []).map(o => o.value))
                  }
                />
                {touched.target_user_ids && errors.target_user_ids && (
                  <div className="text-danger small">{errors.target_user_ids}</div>
                )}
              </Col>
            )}
          </Row>

          <div className="d-flex gap-2 mt-2">
            <ToggleButton active={values.active} onClick={() => setFieldValue("active", !values.active)}>
              {values.active ? "Active" : "Inactive"}
            </ToggleButton>
            <ToggleButton
              active={values.skip_friday}
              color="warning"
              onClick={() => setFieldValue("skip_friday", !values.skip_friday)}
            >
              {values.skip_friday ? "Skips Friday" : "Runs on Friday"}
            </ToggleButton>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex flex-column align-items-stretch">
          {formik.submitCount > 0 && errorList.length > 0 && (
            <div className="text-danger small mb-2">
              <div className="fw-medium">Please fix before saving:</div>
              <ul className="mb-0 ps-3">
                {errorList.map(msg => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" type="button" onClick={toggle}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={createMutation.isPending || editMutation.isPending}
            >
              {editMutation.isPending || createMutation.isPending
                ? "Saving..."
                : isEdit
                  ? "Update Reminder"
                  : "Create Reminder"}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default ReminderAddEdit
